import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Camera, TrendingUp, Users, Zap, BarChart3, CheckCircle, Smartphone, Trophy, Clock, Target, Apple, MessageCircle } from 'lucide-react';
import Footer from './Footer';
import './App.css';
import screenshot from './assets/scouting-page.png';
import serveTracking from './assets/serve-tracking.png';
import receptionTracking from './assets/reception-tracking.png';
import attackTracking from './assets/attack-tracking.png';
import iconLogo from './assets/icon.png';

const VolleyScoutPromo = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [supportHovered, setSupportHovered] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % 3);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const features = [
        {
            icon: <TrendingUp className="feature-icon" />,
            title: "Live-Analyse",
            description: "Erfasse Annahme, Angriff und Aufschlag in Echtzeit während des Spiels",
            color: "#007AFF",
            colorRgb: "0, 122, 255"
        },
        {
            icon: <BarChart3 className="feature-icon" />,
            title: "Detaillierte Statistiken",
            description: "Live Auswertung der Daten und umfangreiche Statistiken für beide Teams",
            color: "#43F36F",
            colorRgb: "67, 243, 111"
        },
        {
            icon: <Users className="feature-icon" />,
            title: "Team-Sharing",
            description: "Co-Trainer können per QR-Code beitreten und in Echtzeit mitanalysieren",
            color: "#FF8A42",
            colorRgb: "255, 138, 66"
        }
    ];

    const benefits = [
        { icon: <Zap />, text: "Keine Vorkenntnisse nötig" },
        { icon: <Clock />, text: "Schnelle Dateneingabe" },
        { icon: <Smartphone />, text: "Optimiert für Tablets" },
        { icon: <CheckCircle />, text: "Automatische Berechnungen" },
        { icon: <Users />, text: "Multi-User Support" },
        { icon: <BarChart3 />, text: "Sofortige Statistiken" }
    ];

    return (
        <div className="app-container">
            {/* Floating Support Button */}
            <Link
                to="/support"
                onMouseEnter={() => setSupportHovered(true)}
                onMouseLeave={() => setSupportHovered(false)}
                style={{
                    position: 'fixed',
                    right: '2rem',
                    bottom: '2rem',
                    zIndex: 999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: supportHovered ? '1rem 1.5rem' : '1rem',
                    background: '#007AFF',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    color: '#edfaff',
                    fontWeight: '600',
                    fontSize: '1rem',
                    boxShadow: '0 4px 20px rgba(0, 122, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    width: supportHovered ? 'auto' : '56px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                }}
            >
                <MessageCircle size={24} style={{ flexShrink: 0 }} />
                <span style={{
                    opacity: supportHovered ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    display: supportHovered ? 'block' : 'none'
                }}>
                    Support
                </span>
            </Link>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    {/* Logo */}
                    <div className="logo-container">
                        <img
                            src={iconLogo}
                            alt="VolleyScout Logo"
                            style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'contain'
                            }}
                        />
                    </div>

                    <h1 className="hero-title">VolleyScout</h1>

                    <p className="hero-subtitle">
                        Die einfache Volleyball-Analyse App <br />
                        für Trainer und Co-Trainer
                    </p>

                    {/* App Store Buttons */}
                    <div className="store-buttons">
                        <button className="store-btn app-store-btn">
                            <Apple size={24} />
                            <div className="store-btn-text">
                                <span className="store-btn-small">Download im</span>
                                <span className="store-btn-large">App Store</span>
                            </div>
                        </button>
                        <button className="store-btn play-store-btn">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                            </svg>
                            <div className="store-btn-text">
                                <span className="store-btn-small">Jetzt bei</span>
                                <span className="store-btn-large">Google Play</span>
                            </div>
                        </button>
                    </div>

                    {/* Feature Highlights */}
                    <div className="feature-grid">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                className={`feature-card ${activeFeature === idx ? 'active' : ''}`}
                                style={{
                                    '--card-color': feature.color,
                                    '--card-color-rgb': feature.colorRgb
                                }}
                            >
                                <div
                                    className="feature-icon-box"
                                    style={{
                                        background: feature.color
                                    }}
                                >
                                    {feature.icon}
                                </div>
                                <h3 className="feature-title">{feature.title}</h3>
                                <p className="feature-description">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Analysis Features Section */}
            <section className="analysis-section">
                <div className="section-container">
                    <div className="section-header">
                        <h2 className="section-title">Vollständige Spielanalyse</h2>
                        <p className="section-subtitle">
                            Erfasse alle wichtigen Spielaktionen mit nur wenigen Klicks
                        </p>
                    </div>

                    {/* Main Screenshot - Tablet Landscape */}
                    <div className="main-screenshot-container">
                        <div className="tablet-landscape">
                            <div className="tablet-screen-landscape">
                                <img
                                    src={screenshot}
                                    alt="VolleyScout Scouting Interface"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 'var(--radius-md)'
                                    }}
                                />
                            </div>
                            <div className="tablet-camera"></div>
                        </div>
                    </div>

                    {/* Aufschlag - Text Links, Bild Rechts */}
                    <div className="feature-detail-section">
                        <div className="feature-detail-content">
                            <div className="feature-detail-icon-box" style={{ background: 'rgba(255, 138, 66, 0.15)' }}>
                                <Trophy className="feature-detail-icon" style={{ color: '#FF8A42' }} />
                            </div>
                            <h3 className="feature-detail-title">Aufschlag</h3>
                            <p className="feature-detail-description">
                                9-Zonen Unterteilung, Aufschlagtypen und Aufschlagrichtung
                            </p>
                            <div className="feature-detail-stats" style={{ background: 'rgba(255, 138, 66, 0.15)', color: '#FF8A42' }}>
                                Ass • in Play • Fehler
                            </div>
                        </div>
                        <div className="feature-detail-image">
                            <img src={serveTracking} alt="Aufschlag Tracking" />
                        </div>
                    </div>

                    {/* Annahme - Text Rechts, Bild Links */}
                    <div className="feature-detail-section reverse">
                        <div className="feature-detail-image">
                            <img src={receptionTracking} alt="Annahme Tracking" />
                        </div>
                        <div className="feature-detail-content">
                            <div className="feature-detail-icon-box" style={{ background: 'rgba(0, 122, 255, 0.15)' }}>
                                <Target className="feature-detail-icon" style={{ color: '#007AFF' }} />
                            </div>
                            <h3 className="feature-detail-title">Annahme</h3>
                            <p className="feature-detail-description">
                                6-stufiges Bewertungssystem mit Positionserkennung
                            </p>
                            <div className="feature-detail-stats" style={{ background: 'rgba(0, 122, 255, 0.15)', color: '#007AFF' }}>
                                0-5 Punkte • Vorne/Hinten
                            </div>
                        </div>
                    </div>

                    {/* Angriff - Text Links, Bild Rechts */}
                    <div className="feature-detail-section">
                        <div className="feature-detail-content">
                            <div className="feature-detail-icon-box" style={{ background: 'rgba(67, 243, 111, 0.15)' }}>
                                <Zap className="feature-detail-icon" style={{ color: '#43F36F' }} />
                            </div>
                            <h3 className="feature-detail-title">Angriff</h3>
                            <p className="feature-detail-description">
                                Erfasse Position, Richtung und Ergebnis jedes Angriffs
                            </p>
                            <div className="feature-detail-stats" style={{ background: 'rgba(67, 243, 111, 0.15)', color: '#43F36F' }}>
                                4 Positionen • diagonal / line
                            </div>
                        </div>
                        <div className="feature-detail-image">
                            <img src={attackTracking} alt="Angriff Tracking" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits-section">
                <div className="section-container">
                    <div className="section-header">
                        <h2 className="section-title">Entwickelt für Trainer</h2>
                        <p className="section-subtitle">
                            Keine komplizierte Software – einfach App öffnen und loslegen
                        </p>
                    </div>

                    <div className="benefits-grid">
                        {benefits.map((benefit, idx) => (
                            <div key={idx} className="benefit-card">
                                <div className="benefit-icon-box">
                                    {React.cloneElement(benefit.icon, { size: 24 })}
                                </div>
                                <p className="benefit-text">{benefit.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-container">
                    <h2 className="cta-title">Bereit für bessere Analysen?</h2>
                    <p className="cta-subtitle">
                        Starte jetzt kostenlos und verbessere deine Spielanalyse
                    </p>

                    {/* Store Buttons wiederholt */}
                    <div className="store-buttons">
                        <button className="store-btn app-store-btn">
                            <Apple size={24} />
                            <div className="store-btn-text">
                                <span className="store-btn-small">Download im</span>
                                <span className="store-btn-large">App Store</span>
                            </div>
                        </button>
                        <button className="store-btn play-store-btn">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                            </svg>
                            <div className="store-btn-text">
                                <span className="store-btn-small">Jetzt bei</span>
                                <span className="store-btn-large">Google Play</span>
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default VolleyScoutPromo;